import React, { useCallback, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Row,
  Col,
  Container,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { createPackage } from "../../api/packages";
import { searchAddresses, geocodeAddress, geocodeDefaultsFor } from "../../utils/geocode";
import { packageStatus } from "../../utils/packageStatus";
import { showToast } from "../../utils/showToast";
import useAuth from "../../hooks/useAuth";
import FormDivider from "../formsDivider";
import Stepper from "../stepper";
import ErrorMessage from "../errorMessage";
import Select from "react-select";
import { getRegions, getCommunes } from "../../api/contracts";
import { useCountry } from "../../context/CountryContext";
import { isValidPostalCode } from "../postalCodeChipsInput";
import PhoneInput from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';

export default function CreatePackage({
  edit,
  defaultData,
  closeModal,
  fetch,
  setConfirmationModal,
  setPackagesCreated,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const methods = useForm({ mode: "onSubmit" });
  const { user } = useAuth();
  const companyShippingAddresses = user?.company?.shippingAddresses || [];

  const { watch, handleSubmit, control, formState, setValue } = methods;
  const { errors } = formState;
  const [name, phone, city, address] = watch([
    "receiver_name",
    "receiver_phone",
    "cityName",
    "street_address",
  ]);

  const isButtonDisabled = !name || !phone || !city || !address || loading;

  const [regions, setRegions] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [isRegionsLoaded, setIsRegionsLoaded] = useState(false);
  // País de operación del usuario. `countryCode` va en MAYÚSCULAS a
  // <PhoneInput defaultCountry> (react-phone-number-input solo acepta el ISO en
  // mayúsculas); el geocoder usa su propia tabla, que ya lo pasa a minúsculas.
  const {
    countryId,
    countryCode,
    countryName,
    postalCodeRates,
    loading: countryLoading,
  } = useCountry();
  // El código postal solo se muestra en países donde significa algo para la operación
  // (hoy Argentina, donde además define la tarifa de pago al repartidor).
  const postalEnabled = postalCodeRates?.enabled === true;

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressDebounceRef = useRef(null);

  // Sesgo de geocodificación del país del operador (país + punto de foco).
  const geoDefaults = geocodeDefaultsFor(countryCode);

  const buscarSugerencias = useCallback((query) => {
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }
    const term = (query || "").trim();
    if (term.length < 3) {
      setAddressSuggestions([]);
      setAddressLoading(false);
      return;
    }
    addressDebounceRef.current = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const data = await searchAddresses(term, geoDefaults);
        setAddressSuggestions(Array.isArray(data) ? data : []);
      } catch (error) {
        setAddressSuggestions([]);
      } finally {
        setAddressLoading(false);
      }
    }, 300);
  }, [geoDefaults]);

  useEffect(() => {
    // Guard: el catálogo se pide recién con el país del usuario resuelto.
    if (countryLoading || !countryId) return;
    const fetchRegionsData = async () => {
      const data = await getRegions({ countryId });
      if (!data.error) {
        setRegions(data.map(r => ({ value: r.code, label: r.name })));
      }
      setIsRegionsLoaded(true);
    };
    fetchRegionsData();
  }, [countryId, countryLoading]);

  const selectedRegion = watch("regionCode");

  useEffect(() => {
    if (selectedRegion && countryId) {
      const fetchCommunesData = async () => {
        const data = await getCommunes({ countryId, regionId: selectedRegion });
        if (!data.error) {
          setCommunes(data.map(c => ({ value: c.name, label: c.name })));
        }
      };
      fetchCommunesData();
    }
  }, [selectedRegion, countryId]);

  useEffect(() => {
    if (edit && defaultData && isRegionsLoaded) {
      const defaultRegionName = defaultData?.shipping_address?.city?.name;
      const defaultRegion = regions.find(r => r.label === defaultRegionName);
      if (defaultRegion) {
        setValue("regionCode", defaultRegion.value);
      }
      const defaultCommune = defaultData?.shipping_address?.municipality?.name;
      setValue("cityName", defaultCommune);
      // El CP se guarda crudo; en la vista de detalle se muestra tal cual llegó.
      setValue("zip_code", defaultData?.shipping_address?.zip_code ?? "");
    }
  }, [edit, defaultData, isRegionsLoaded, regions, setValue]);

  const submitValues = useCallback(
    async (values) => {
      setLoading(true);
      try {
        const geo = await geocodeAddress(values.street_address, geoDefaults);
        if (!geo) {
          throw new Error(t("Dirección inválida"));
        }
        const latLng = { lat: geo.lat, lng: geo.lng };

        // Separa nombre de calle y número desde la dirección ingresada
        const streetMatch = values.street_address.match(/^\s*(.+?)\s+(\d+)\b/);
        const parsedStreetName = streetMatch ? streetMatch[1].trim() : values.street_address.trim();
        let parsedStreetNumber = streetMatch ? streetMatch[2] : '';

        const aptMatch = values.street_address.match(/\b(depto|dpto|dept|apto|apt|departamento)\b\.?\s*(\d+)/i);
        const apt = aptMatch ? aptMatch[2] : null;
        if (apt) {
          parsedStreetNumber = `${parsedStreetNumber}, depto ${apt}`;
        }

        const selectedAddress = values.senderAddress.value;
        const packageDimensions = {
          height: values?.alto_paquete,
          width: values?.ancho_paquete,
          length: values?.largo_paquete,
          weight: values?.peso_paquete,
        };
        const regionName = regions.find(r => r.value === values.regionCode)?.label || "";
        const parsedValues = {
          receiver_lastName: values?.receiver_lastName,
          receiver_name: values?.receiver_name,
          receiver_phone: values?.receiver_phone,
          senderAddress: selectedAddress.address,
          senderNeighborhood: selectedAddress.municipality,
          senderState: selectedAddress.state,
          status: 0,
          type: "OTS",
          packageDimensions: packageDimensions,
          shipping_address: {
            street_name: parsedStreetName,
            street_number: parsedStreetNumber.trim(),
            lat: latLng.lat,
            lng: latLng.lng,
            city: {
              name: regionName,
            },
            municipality: {
              name: values?.cityName,
            },
            // country es requerido por el DTO del backend (ShippingAddress.country);
            // sin él la validación devuelve 400 antes de insertPackage.
            country: { name: countryName },
            delivery_preference: values?.delivery_preference,
            comment: values?.comment,
          },
        };

        // Código postal declarado. Se envía CRUDO (trim + uppercase) y se omite vacío:
        // el backend distingue "sin CP" de "CP vacío".
        // NOTA: hoy este submit es inalcanzable — el único render de <CreatePackage> es
        // el modal de DETALLE (`edit`), donde el botón de crear no se monta y todos los
        // campos van deshabilitados. Se cablea igual para que el campo no quede a medias
        // si alguien revive el alta por acá (el alta viva es createPackageWizard).
        const rawZip = String(values?.zip_code ?? "").trim().toUpperCase();
        if (rawZip) {
          parsedValues.shipping_address.zip_code = rawZip;
        }

        if (values?.order_id) {
          parsedValues.order_id = values.order_id;
        }

        const response = await createPackage({ values: parsedValues });

        if (response && response.data) {
          setConfirmationModal(true);
          setPackagesCreated([response.data]);
          closeModal(false);
          fetch(true);
        } else {
          let err = response ? (response.error || response) : null;
          let errorMsg;
          if (err && err.message === "Order ID exists for this company") {
            errorMsg = t("El número de venta ya existe, ingrese un número de venta diferente.");
          } else {
            const msg = err ? err.message : null;
            errorMsg = msg ? (Array.isArray(msg) ? msg.join('\n') : msg) : t("Error al crear el paquete, por favor inténtelo nuevamente.");
          }
          showToast(errorMsg, false);
        }
      } catch (error) {
        let errorMsg = t("Error al crear el envío");
        console.log("error al crear paquete: ", error)
        if (error.message === "Order ID exists for this company") {
          errorMsg = t("El número de venta ya existe, ingrese un número de venta diferente.");
        } else if (error.message) {
          errorMsg = error.message;
        }
        showToast(errorMsg, false);
      } finally {
        setLoading(false);
      }
    },
    [closeModal, fetch, setConfirmationModal, setPackagesCreated, regions, countryName, geoDefaults]
  );

  const stepsValue = { step1: { value: defaultData?.status } };

  return (
    <Container className="mt-3">
      <Form onSubmit={handleSubmit(submitValues)}>
        {edit && (
          <Label className="forms-following-number-title">
            {t("Pedido")} {defaultData.followingNumber}
          </Label>
        )}
        <FormDivider message={t("Datos del emisor")} />
        <Row>
          <Col xs="12" md="12" xl="12" className="mb-3">
            <Label className="forms-input-title">{t("DIRECCIÓN DE RETIRO")}</Label>
            <Controller
              name="senderAddress"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={companyShippingAddresses.map((address) => ({
                    value: address,
                    label: `${address.address}, ${address.municipality}, ${address.state}`.toUpperCase(),
                  }))}
                  placeholder={t("Selecciona la dirección de retiro")}
                  isDisabled={edit}
                  value={field.value}
                />
              )}
              rules={{ required: t("Seleccione la dirección de retiro") }}
            />
            {errors?.senderAddress && (
              <ErrorMessage msg={errors?.senderAddress?.message} />
            )}
          </Col>

          <FormDivider message={t("Datos de quién recibe")} />

          <Row>
            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("NOMBRE COMPLETO")}</Label>
              <Controller
                name="receiver_name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    className="p-2 px-3 forms-custom-input"
                    placeholder={t("Escribe el nombre completo")}
                    disabled={edit}
                    defaultValue={`${defaultData?.receiver_name ?? ""} ${defaultData?.receiver_lastName ?? ""}`}
                  />
                )}
                rules={{ required: t("Ingrese el nombre completo") }}
              />
              {errors?.receiver_name && (
                <ErrorMessage msg={errors?.receiver_name?.message} />
              )}
            </Col>

            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("TELÉFONO")}</Label>
              <Controller
                name="receiver_phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    placeholder={t("Ingresa el número de teléfono")}
                    disabled={edit}
                    className="form-control p-2 px-3 forms-custom-input"
                    defaultCountry={countryCode}
                    countries={['CL', 'AR', 'MX', 'PE', 'BR']} // Ejemplo de ciertos prefijos; ajusta la lista
                  />
                )}
                rules={{
                  required: t("Ingrese el número de teléfono"),
                  validate: (value) => {
                    if (!value) return t("Número de teléfono requerido");
                    const phoneNumber = value.replace(/\D/g, '');
                    // Prefijo telefónico (dígitos), no confundir con el ISO
                    // `countryCode` del país de operación que usa el componente.
                    const phonePrefix = value.match(/^\+(\d+)/)?.[1];
                    if (phonePrefix === "56" && phoneNumber.length !== 11) {
                      return t("El número chileno debe tener 9 dígitos después del +56");
                    }
                    // Agrega validaciones para otros países si es necesario
                    return true;
                  },
                }}
              />
            </Col>
          </Row>

          <Row>
            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("NÚMERO DE VENTA")}</Label>
              <Controller
                name="order_id"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    className="p-2 px-3 forms-custom-input"
                    placeholder={t("Escribe la referencia de tu venta")}
                    disabled={edit}
                    defaultValue={defaultData?.order_id}
                  />
                )}
              />
            </Col>

            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("REGIÓN DE ENTREGA")}</Label>
              <Controller
                name="regionCode"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={regions}
                    placeholder={t("Selecciona la región")}
                    isDisabled={edit}
                    value={regions.find(r => r.value === field.value)}
                    onChange={(selected) => field.onChange(selected ? selected.value : null)}
                  />
                )}
                rules={{ required: t("Seleccione región de entrega") }}
              />
              {errors?.regionCode && (
                <ErrorMessage msg={errors?.regionCode?.message} />
              )}
            </Col>
          </Row>

          <Row>
            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("CIUDAD/COMUNA DE ENTREGA")}</Label>
              <Controller
                name="cityName"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={communes}
                    placeholder={t("Selecciona la comuna")}
                    isDisabled={edit || !selectedRegion}
                    value={communes.find(c => c.value === field.value)}
                    onChange={(selected) => field.onChange(selected ? selected.value : null)}
                  />
                )}
                rules={{ required: t("Seleccione ciudad/comuna de entrega") }}
              />
              {errors?.cityName && (
                <ErrorMessage msg={errors?.cityName?.message} />
              )}
            </Col>

            {postalEnabled && (
              <Col xs="6" md="6" xl="6">
                <Label className="forms-input-title">{t("CÓDIGO POSTAL")}</Label>
                <Controller
                  name="zip_code"
                  control={control}
                  rules={{
                    validate: (v) =>
                      !v ||
                      String(v).trim() === "" ||
                      isValidPostalCode(v) ||
                      t("Usa un CPA (M5500ABC) o un código de 4 dígitos (5500)"),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      className="p-2 px-3 forms-custom-input"
                      placeholder={t("Ej: 5500 o M5500ABC")}
                      disabled={edit}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    />
                  )}
                />
                {errors?.zip_code && (
                  <ErrorMessage msg={errors?.zip_code?.message} />
                )}
              </Col>
            )}

            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("DIRECCIÓN DE ENTREGA")}</Label>
              <Controller
                name="street_address"
                control={control}
                render={({ field }) => (
                  <div>
                    <Input
                      type="text"
                      className="p-2 px-3 forms-custom-input"
                      placeholder={t("Escribe la dirección completa")}
                      disabled={edit}
                      value={field.value || ""}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        buscarSugerencias(e.target.value);
                      }}
                    />
                    <div className="autocomplete-dropdown-container">
                      {addressLoading && <div>{t("Cargando...")}</div>}
                      {addressSuggestions.map((suggestion, index) => {
                        const text = [suggestion.label, suggestion.subtitle]
                          .filter(Boolean)
                          .join(", ");
                        return (
                          <div
                            className="suggestion-item"
                            style={{
                              backgroundColor: "#ffffff",
                              cursor: "pointer",
                              padding: "10px",
                            }}
                            key={`${suggestion.id}-${index}`}
                            onClick={() => {
                              field.onChange(text);
                              setAddressSuggestions([]);
                            }}
                          >
                            <span>{text}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                rules={{ required: t("Ingrese dirección de entrega") }}
              />
              {errors?.street_address && (
                <ErrorMessage msg={errors?.street_address?.message} />
              )}
            </Col>
          </Row>

          <Row className="mt-3 w-100">
            <Col xs="12" md="6" className="mb-3">
              <Label className="forms-input-title">{t("TAMAÑO DEL PAQUETE")}</Label>
              <Row>
                <Col xs="6" md="4">
                  <Controller
                    name="alto_paquete"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        step="1"
                        min="0"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("Alto")}
                        disabled={edit}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                        }
                      />
                    )}
                  />
                  {errors?.alto_paquete && (
                    <ErrorMessage msg={errors?.alto_paquete?.message} />
                  )}
                </Col>
                <Col xs="6" md="4">
                  <Controller
                    name="ancho_paquete"
                    control={control}
                    render={({ field }) => (
                        <Input
                          {...field}
                          type="number"
                          step="1"
                          min="0"
                          className="p-2 px-3 forms-custom-input"
                          placeholder={t("Ancho")}
                          disabled={edit}
                          onChange={(e) =>
                            field.onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                          }
                        />
                      )}
                    />
                  {errors?.ancho_paquete && (
                    <ErrorMessage msg={errors?.ancho_paquete?.message} />
                  )}
                </Col>
                <Col xs="6" md="4">
                  <Controller
                    name="largo_paquete"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        step="1"
                        min="0"
                        className="p-2 px-3 forms-custom-input"
                        placeholder={t("Largo")}
                        disabled={edit}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                        }
                      />
                    )}
                  />
                  {errors?.largo_paquete && (
                    <ErrorMessage msg={errors?.largo_paquete?.message} />
                  )}
                </Col>
              </Row>
            </Col>

            <Col xs="6" md="6" xl="6">
              <Label className="forms-input-title">{t("PESO DEL PAQUETE (kg)")}</Label>
              <Controller
                name="peso_paquete"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    step="0.1"
                    min="0"
                    className="p-2 px-3 forms-custom-input"
                    placeholder={t("Peso en kg")}
                    disabled={edit}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value))
                    }
                  />
                )}
              />
              {errors?.peso_paquete && (
                <ErrorMessage msg={errors?.peso_paquete?.message} />
              )}
            </Col>
          </Row>

          <Col xs="12" md="12" xl="12" className="mt-3 mb-5">
            <Label className="forms-input-title">{t("COMENTARIO")}</Label>
            <Controller
              name="comment"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="text"
                  className="p-2 px-3 forms-custom-input-full-width"
                  placeholder={t("Escribe una referencia o nota")}
                  disabled={edit}
                  defaultValue={defaultData?.shipping_address?.comment}
                />
              )}
            />
          </Col>
        </Row>

        {!edit && (
          <Button
            block
            color="dark"
            className="forms-custom-sender-button bg-black"
            disabled={isButtonDisabled}
          >
            {t("Crear encomienda")} {loading && <Spinner size="sm" />}
          </Button>
        )}

        {edit && (
          <Row>
            <Col xs="12" md="12" xl="12">
              <Row>
                <Col xs="3" md="3" xl="3" className="d-grid">
                  <Label className="forms-input-detail-view">{t("ESTADO")}</Label>
                  <Label className="forms-input-detail-view__state-data">
                    {t(packageStatus[defaultData?.status])}
                  </Label>
                </Col>
                <Col xs="6" md="6" xl="6" className="d-grid">
                  <Label className="forms-input-title">
                    {t("HORA ESTIMADA DE LLEGADA")}
                  </Label>
                  <Label className="forms-input-detail-view__time">
                    {t("Entre: 14:00 y 21:00")}
                  </Label>
                </Col>
              </Row>
            </Col>
            <Col xs="12" md="12" xl="12">
              <Row>
                <Label className="forms-input-detail-view mt-4">
                  {t("HISTORIAL DEL PEDIDO")}
                </Label>
                <Stepper stepStatus={stepsValue} />
              </Row>
            </Col>
          </Row>
        )}
      </Form>
    </Container>
  );
}